import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import AppHeader from "@/components/AppHeader";
import Footer from "@/components/Footer";
import { Helmet } from "react-helmet-async";
import { useHostname } from "@/lib/ssr";
import { baseLang, siteOrigin } from "@/lib/seo/site";
import { Input } from "@/components/ui/input";
import { Calendar, ExternalLink, Loader2, Image, ArrowLeft, Search, Clock } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import galleryPlaceholder from "@/assets/gallery-placeholder.jpg";
import NotFound from "@/pages/NotFound";
import {
  DIRECTORY_PAGE_SIZE,
  companyKey,
  fetchGalleryList,
  galleryDate,
  galleryLocation,
  hitCursor,
  type GalleryHit,
  type GalleryListPage,
} from "@/lib/gallery/directory";

/**
 * /find-your-photos/<company>: one photographer's public galleries. The first page is prefetched on the server
 * (src/lib/gallery/ssrPrefetch.ts), so the galleries are real links in the HTML, and a company with no public
 * galleries is a real 404 (never a "soft 404" page with a self-canonical).
 */
const CompanyGalleries = () => {
  const { companyName } = useParams<{ companyName: string }>();
  const { t, i18n } = useTranslation();
  const lang = baseLang(i18n.language);
  const queryClient = useQueryClient();
  let decodedName = companyName || "";
  try {
    decodedName = decodeURIComponent(decodedName);
  } catch {
    /* keep the raw segment */
  }
  const origin = siteOrigin(useHostname());
  const seoTitle = `${decodedName} event photos | Photohawk`;
  const seoDescription = `Event photo galleries from ${decodedName} on Photohawk. Find your photos by bib number or selfie.`;
  const seoUrl = `${origin}/find-your-photos/${encodeURIComponent(decodedName)}`;

  const initial = queryClient.getQueryData<GalleryListPage>(companyKey(decodedName));
  const [allHits, setAllHits] = useState<GalleryHit[]>(initial?.hits ?? []);
  const [loading, setLoading] = useState(!initial);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(initial ? initial.hits.length >= DIRECTORY_PAGE_SIZE : true);
  const [searchQuery, setSearchQuery] = useState("");
  const skipFirstFetch = useRef(!!initial);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const abortRef = useRef<AbortController | null>(null);

  const fetchGalleries = useCallback(async (searchAfter?: string, append = false) => {
    if (append) setLoadingMore(true);
    else setLoading(true);

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const data = await fetchGalleryList({ companyName: decodedName, searchAfter }, { signal: controller.signal });
      if (append) {
        setAllHits((prev) => [...prev, ...data.hits]);
      } else {
        setAllHits(data.hits);
      }
      setHasMore(data.hits.length >= DIRECTORY_PAGE_SIZE);
    } catch (err: unknown) {
      if ((err as { name?: string })?.name !== "AbortError") console.error("Failed to fetch galleries:", err);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [decodedName]);

  useEffect(() => {
    if (skipFirstFetch.current) {
      skipFirstFetch.current = false;
      return;
    }
    setAllHits([]);
    setHasMore(true);
    fetchGalleries();
  }, [fetchGalleries]);

  useEffect(() => {
    if (!sentinelRef.current) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const cursor = hitCursor(allHits[allHits.length - 1]);
        if (entries[0].isIntersecting && hasMore && !loading && !loadingMore && cursor) fetchGalleries(cursor, true);
      },
      { rootMargin: "200px" }
    );
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [hasMore, loading, loadingMore, allHits, fetchGalleries]);

  // A company with no public galleries (the server asked the API): a real 404.
  if (initial && initial.total === 0) return <NotFound />;

  // Client-side filtering by search query
  const filteredHits = searchQuery.trim()
    ? allHits.filter((hit) =>
        hit.name.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : allHits;

  const now = Date.now();
  const isFuture = (h: GalleryHit) => new Date(typeof h.date === "number" ? h.date : String(h.date)).getTime() > now;
  const upcomingHits = filteredHits.filter(isFuture);
  const pastHits = filteredHits.filter((hit) => !isFuture(hit));

  const renderCard = (hit: GalleryHit, index: number, isUpcoming = false) => (
    <a key={`${hit.url}-${index}`} href={hit.url} target="_blank" rel="noopener noreferrer" className="group">
      <Card className={`overflow-hidden h-full hover:shadow-xl transition-shadow duration-300 border-border ${isUpcoming ? "opacity-80" : ""}`}>
        <div className="relative aspect-[4/3] bg-muted overflow-hidden">
          <img
            src={hit.media?.featuredImage || galleryPlaceholder}
            alt={hit.name}
            loading={index < 4 ? "eager" : "lazy"}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            style={hit.mediaBackgroundPosition ? { objectPosition: hit.mediaBackgroundPosition } : undefined}
          />
          {isUpcoming && (
            <Badge className="absolute top-3 right-3 bg-hawk-orange/90 text-white border-0">
              <Clock size={12} className="mr-1" />
              {t("findPhotos.upcoming")}
            </Badge>
          )}
        </div>
        <div className="p-4">
          <h3 className="font-display font-semibold text-foreground group-hover:text-hawk-orange transition-colors line-clamp-2 mb-1">
            {hit.name}
          </h3>
          <p className="text-xs text-muted-foreground line-clamp-1 mb-2">{galleryLocation(hit, lang)}</p>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <Calendar size={14} />
              <span>{galleryDate(hit, lang)}</span>
            </div>
            <div className="flex items-center gap-3">
              {!isUpcoming && hit.noItems != null && (
                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                  <Image size={14} />
                  <span>{hit.noItems.toLocaleString("en-GB")}</span>
                </div>
              )}
              <ExternalLink size={14} className="text-muted-foreground group-hover:text-hawk-orange transition-colors" />
            </div>
          </div>
        </div>
      </Card>
    </a>
  );

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDescription} />
        <meta name="robots" content="index, follow, max-image-preview:large" />
        <link rel="canonical" href={seoUrl} />
        <meta property="og:url" content={seoUrl} />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDescription} />
        <meta name="twitter:title" content={seoTitle} />
        <meta name="twitter:description" content={seoDescription} />
      </Helmet>
      <AppHeader variant="marketing" />

      <section className="pt-32 pb-12 bg-gradient-to-b from-hawk-navy to-background">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto text-center">
            <Link
              to="/find-your-photos"
              className="inline-flex items-center gap-1.5 text-white/60 hover:text-white text-sm mb-6 transition-colors"
            >
              <ArrowLeft size={16} />
              {t("findPhotos.title")}
            </Link>
            <h1 className="text-4xl md:text-5xl font-display font-bold text-white mb-2">
              {decodedName}
            </h1>
            <p className="text-white/70 mb-8">
              {t("findPhotos.galleries")}
            </p>

            {/* Search within this company */}
            <div className="relative max-w-lg mx-auto">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" size={20} />
              <Input
                type="text"
                placeholder={t("findPhotos.searchCompany", { company: decodedName })}
                aria-label={t("findPhotos.searchCompany", { company: decodedName })}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-12 h-14 text-lg rounded-xl bg-card border-border shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="py-12">
        <div className="container mx-auto px-4">
          {!loading && (
            <p className="text-sm text-muted-foreground mb-6 max-w-6xl mx-auto">
              {(searchQuery.trim() ? filteredHits.length : initial?.total ?? filteredHits.length).toLocaleString("en-GB")} {t("findPhotos.galleries")}
            </p>
          )}

          {loading && (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="animate-spin text-hawk-orange" size={32} />
            </div>
          )}

          {!loading && (
            <div className="space-y-16 max-w-6xl mx-auto">
              {upcomingHits.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-6">
                    <Clock size={20} className="text-hawk-orange" />
                    <h2 className="text-xl font-display font-bold text-foreground">{t("findPhotos.upcomingGalleries")}</h2>
                    <span className="text-sm text-muted-foreground">({upcomingHits.length})</span>
                  </div>
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                    {upcomingHits.map((hit, i) => renderCard(hit, i, true))}
                  </div>
                </div>
              )}

              {pastHits.length > 0 && (
                <div>
                  {upcomingHits.length > 0 && (
                    <div className="flex items-center gap-2 mb-6">
                      <Image size={20} className="text-hawk-orange" />
                      <h2 className="text-xl font-display font-bold text-foreground">{t("findPhotos.availableGalleries")}</h2>
                      <span className="text-sm text-muted-foreground">({pastHits.length})</span>
                    </div>
                  )}
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                    {pastHits.map((hit, i) => renderCard(hit, i))}
                  </div>
                </div>
              )}
            </div>
          )}

          {!loading && filteredHits.length === 0 && (
            <div className="text-center py-20">
              <p className="text-muted-foreground">{searchQuery ? t("findPhotos.noMatch") : t("findPhotos.noResultsDesc")}</p>
            </div>
          )}

          <div ref={sentinelRef} className="h-4" />

          {loadingMore && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="animate-spin text-hawk-orange" size={24} />
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CompanyGalleries;
